<template>
	<v-container>
		<v-row class="contentRow" align="center">
			<v-col>
				<h1>Self Assessment Score of Your Connections</h1>
			</v-col>
		</v-row>
		<p style="margin: 30px 20px;">
			Explore your connections' sustainability scores in detail under this tab.
			This score acts as a standard for measuring progress and ensuring
			adherence to responsible business practices, aligning with the principles
			outlined in the United Nations Global Compacts for a Sustainable Business
			life. Here your connections' score awaits your attention to have insights
			into the sustainability score of your supply chain.
		</p>
		<div style="display: flex;">
			<v-col align="end">
				<h3 style="margin-right: 60px;">Sort by : </h3>
			</v-col>
			<v-col>
				<v-select v-model="selectedSort" :items="dropdownItems" item-text="title" item-value="title"
					placeholder="Select sorting criteria" style="width: 200px; margin-top: -5px; margin-left: -60px;"
					dense outlined @change="handleSortChange"></v-select>
			</v-col>
			<v-col align="end">
				<h3 style="margin-right: 60px;">Sort by Project : </h3>
			</v-col>
			<v-col>
				<v-select
				v-model="selectedProjectSort"
				:items="projects"
				item-text="name"
				item-value="id"
				placeholder="Select project"
				style="width: 200px; margin-top: -5px; margin-left: -60px;"
				dense
				outlined
				@change="handleProjectSortChange"
				></v-select>
			</v-col>
		</div>

		<v-card>
			<v-card-title>
				<v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line
					hide-details></v-text-field>
			</v-card-title>
			<v-data-table theme--light :headers="headers" :items="connections" :search="search" :items-per-page="10"
				class="light table" :footer-props="{
					'items-per-page-options': [10, 15, 20],
				}">
				<template v-slot:item.averageScore="{ item }">
					<v-progress-circular :rotate="360" :size="40" :width="5" :value="item.averageScore"
						:color="getColorOnScore(item.averageScore)">
						{{ item.averageScore }}
					</v-progress-circular>
				</template>
				<template v-slot:item.importanceofsupplier="{ item }">
					<v-select class="smalldropdown" style="max-width: 150px;" :items="importanceOptions"
						v-model="item.importanceofsupplier" @change="logImportanceChange(item)">
						<template v-slot:item="{ item }">
							<v-list-item-content>
								<v-list-item-title>
									<v-icon color="primary" size="10" class="mr-2" v-for="i in item.icon" :key="i">fas
										fa-circle</v-icon>
								</v-list-item-title>
							</v-list-item-content>
						</template>
						<template v-slot:selection="{ item }">
							<v-icon color="primary" size="10" class="mr-1 ml-2" v-for="i in item.icon" :key="i">fas
								fa-circle</v-icon>
						</template>
					</v-select>
				</template>
				<template v-slot:item.suppliersfilledout="{ item }">
					<span style="padding-left: 30px;">
						<v-tooltip top>
							<template v-slot:activator="{ on, attrs }">
								<span v-bind="attrs" v-on="on">
									{{ item.noOfSuppliersFilled == null ? 0 : item.noOfSuppliersFilled }}/{{ item.noConnectedsuplliers == null ? 0 : item.noConnectedsuplliers }}
								</span>
							</template>
							<span>No of connections filled out/Total no of connections</span>
						</v-tooltip>
					</span>
				</template>

				<template v-slot:item.countryofoperation="{ item }">
					<span style="padding-left: 20px;">{{ item.countryofoperation }}</span>
				</template>
				<template v-slot:item.riskImpactScore="{ item }">
					<span :style="getRiskImpactStyle(item.riskImpactScore)">{{
						item.riskImpactScore ? item.riskImpactScore : "Not Avilable"
					}}</span>
				</template>
				<template v-slot:item.SupplierScore="{ item }">
					<v-progress-circular class="score-circle" :rotate="360" :size="40" :width="5" :value="item.supplierScore"
						:color="getColorOnScore(item.supplierScore)" @click="openDialog(item)">
						{{ item.supplierScore }}
					</v-progress-circular>
				</template>
				<!--Change color rule!-->
				<template v-slot:item.sortbyscore="{ item }">
					<v-progress-circular :rotate="360" :size="40" :width="5" :value="item.sortbyscore"
						:color="getColor(item.impact)">
						{{ item.averageScore }}
					</v-progress-circular>
				</template>
				<template v-slot:item.filledoutdate="{ item }">
					<span v-if="isDateMoreThanOneYearOld(item.filledoutdate)"
						:style="getFilledOutDateStyle(item.filledoutdate)">
						<v-tooltip top>
							<template v-slot:activator="{ on, attrs }">
								<span v-bind="attrs" v-on="on">
									{{ item.filledoutdate }}
								</span>
							</template>
							<span>Last filled out date is more than 1 year</span>
						</v-tooltip>
					</span>
					<span v-else :style="getFilledOutDateStyle(item.filledoutdate)">
						{{ item.filledoutdate }}
					</span>
				</template>
			</v-data-table>
		</v-card>
		<v-dialog v-model="dialog" max-width="720px" @close="resetCheckboxes">
			<v-card class="score-dialog text-center">
				<v-btn class="score-dialog-close" icon @click="dialog = false">
					<v-icon large color="primary">fas fa-times</v-icon>
				</v-btn>
				<supplier-csr compact :score1="dialogItem.score1" :score2="dialogItem.score2"
					:score3="dialogItem.score3" :score4="dialogItem.score4" :title="`Sustainability Score`" />
				<!-- Conditionally render the h4 and v-btn based on supplierScore -->
				<v-col v-if="dialogItem.supplierScore <= 50 && dialogItem.filledoutdate != 'N/A' && !this.isDateMoreThanOneYearOld(dialogItem.filledoutdate)">
					<h4 style="margin-top: 20px; padding-left: 10px; padding-right: 10px; margin-bottom: 4px;">
						This supplier has a total score below 50%. Would you like to send them an audit and follow up on
						this?
					</h4>
					<div class="score-dialog-audit">
						<v-btn class="score-dialog-btn" elevation="0"
							style="color: white; background-color: orange; padding: 5px; margin-top: 16px !important; font-weight: bolder;"
							exact :disabled="dialogItem.csrid == 0" :to="{ path: '/audit/auditor' }">
							Send audit
						</v-btn>
					</div>
					<p style="margin-top: 10px; padding-left: 10px; padding-right: 10px; text-align: left;">
						After sending the audit, please check the boxes accordingly to send a request for the survey to
						be
						completed again.
					</p>
					<v-checkbox class="mt-0" dense v-model="checkbox1" :disabled="dialogItem.csrid == 0"
						label="Were you able to successfully send the audit to this supplier?"
						style="margin-left: 15px;">
					</v-checkbox>
					<v-checkbox class="mt-0" dense v-model="checkbox2" :disabled="dialogItem.csrid == 0"
						label="Did your supplier complete the audit and send it back to you?"
						style="margin-left: 15px;">
					</v-checkbox>
					<v-checkbox class="mt-0" dense v-model="checkbox3" :disabled="dialogItem.csrid == 0"
						label="Have you assessed the audit?" style="margin-left: 15px;">
					</v-checkbox>
					<v-checkbox class="mt-0" dense v-model="checkbox4" :disabled="dialogItem.csrid == 0"
						label="Do you feel that the supplier has a strong vision or has shown progress?"
						style="margin-left: 15px;">
					</v-checkbox>
					<p style="margin-top: 20px; padding-left: 10px; padding-right: 10px; text-align: left;">
						Send a request to your suppliers to complete the sustainability survey again.
					</p>
				</v-col>
				<v-col v-if="dialogItem.supplierScore <= 50 && dialogItem.filledoutdate != 'N/A' && !this.isDateMoreThanOneYearOld(dialogItem.filledoutdate)">
					<div class="score-dialog-footer score-dialog-footer--again">
						<h4 :style="getFilledOutDateStyleind(dialogItem.filledoutdate)">
							Filled out : {{ dialogItem.filledoutdate }}
						</h4>
						<v-btn class="score-dialog-btn" elevation="0"
							style="color: white; background-color: orange; padding: 5px; margin-top: 12px !important; font-weight: bolder;"
							exact :disabled="(!this.checkbox1 || !this.checkbox2 || !this.checkbox3 || !this.checkbox4)"
							@click="resubmit(dialogItem.clientID, dialogItem.csrid)">
							Request to Fill out Again
						</v-btn>
					</div>
				</v-col>
				<v-col v-if="dialogItem.supplierScore > 50 && !this.isDateMoreThanOneYearOld(dialogItem.filledoutdate)">
					<h4 class="score-dialog-date">
						Filled out : {{ dialogItem.filledoutdate }}
					</h4>
				</v-col>
				<v-col
					v-else-if="this.isDateMoreThanOneYearOld(dialogItem.filledoutdate)">
					<p class="score-dialog-message">
						This supplier has a score that is more than 12 months old, do you like to invite the company to
						fill out
						the Sustainability score again.
					</p>
					<div class="score-dialog-footer">
						<h4 :style="getFilledOutDateStyleind1(dialogItem.filledoutdate)">
							Filled out : {{ dialogItem.filledoutdate }}
						</h4>
						<v-btn class="yourscorebtn score-dialog-btn" elevation="0"
							style="color: white; background-color: orange; padding: 5px;"
							exact :disabled="dialogItem.csrid == 0" @click="notify(dialogItem.clientID)">
							Request to fillout for {{ dialogItem.filledoutyear }}
						</v-btn>
					</div>
				</v-col>
				<v-col
					v-else-if=" dialogItem.supplierScore <= 50 && dialogItem.filledoutdate == 'N/A'">
					<div class="score-dialog-footer score-dialog-footer--center">
						<v-btn class="yourscorebtn score-dialog-btn" elevation="0"
							style="color: white; background-color: orange; padding: 5px;"
							exact  @click="notify(dialogItem.clientID)">
							Request to fillout for Current Year
						</v-btn>
					</div>
				</v-col>

			</v-card>
		</v-dialog>
		<v-dialog v-model="dialogComplete" max-width="550px">
			<v-card class="d-flex flex-column align-center justify-center">
				<v-card-title class="text-center">
					Success! Request Sent to your Supplier
				</v-card-title>

				<v-card-actions class="justify-center">
					<v-btn color="primary" text @click="Complete">OK</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import SupplierAPI from "../../api/csr/supplier"
import CSR from "../../api/csr/csr"
import { SupplierImpactAPI } from "../../api/suppliers/supplierimpact"
import LoadingState from "../../consts/LoadingState"
import ConnectionAPI from "../../api/connections/connection"
export default {
	data() {
		return {
			importanceOptions: [
				{ text: "HIGH", icon: 3 },
				{ text: "MEDIUM", icon: 2 },
				{ text: "LOW", icon: 1 },
			],
			selectedSort: "Total score",
			checkbox1: false,
			checkbox2: false,
			checkbox3: false,
			checkbox4: false,
			connections: [],
			avgScore: [],
			importanceClientArray: [],
			dropdownItems: [
				{ title: "Total score", action: this.sortByTotalScore },
				{ title: "Human rights", action: this.sortByHumanRights },
				{ title: "Labour", action: this.sortByLabour },
				{ title: "Environment", action: this.sortByEnvironment },
				{ title: "Anticorruption", action: this.sortByAnticorruption },
				{ title: "Transparency Law", action: this.sortByTransparencyLaw },
				{ title: "Impact Level", action: this.sortByImportance },
			],
			search: "",
			dialog: false,
			dialogComplete: false,
			dialogItem: {},
			projects: [],
			selectedProjectSort: null,
			selectedProject: null,
			allConnections: [],
			headers: [
				{
					text: "Supplier",
					align: "start",
					value: "name",
					sortable: false,
				},
				{
					text: "Country of Operation",
					value: "countryofoperation",
					sortable: false,
				},
				{
					text: "Company Score",
					value: "SupplierScore",
					sortable: false,
				},
				{
					text: "Suppliers filled out",
					value: "suppliersfilledout",
					sortable: false,
				},
				{
					text: "Supply Chain Score",
					value: "averageScore",
					sortable: false,
				},
				{
					text: "Impact of supplier",
					value: "importanceofsupplier",
					sortable: false,
				},
				{
					text: "Filled Out Date",
					value: "filledoutdate",
					sortable: false,
				},
			],
		}
	},
	async mounted() {
		await this.loadItems()
		await this.loadProjects()
		this.allConnections = [...this.connections]
	},
	watch: {
		dialog(val) {
			if (!val) {
				this.resetCheckboxes()
			}
		}
	},
	methods: {
		async loadItems() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			await SupplierAPI.getSuppliers()
				.then((data) => {
					this.connections = []
					this.importanceClientArray = []
					for (let entry of data) {
						const importance = this.getImportance(entry.importance)
						let importanceScore = 0
						if (entry.importance != null) {
							importanceScore = entry.importance
						}
						let supplierScore = 0
						let score1 = 0
						let score2 = 0
						let score3 = 0
						let score4 = 0
						let csrid = 0
						let filledoutdate = "N/A"
						let filledoutyear = "N/A"
						if (
							entry.csr &&
							entry.csr.score1 !== undefined &&
							entry.csr.score2 !== undefined &&
							entry.csr.score3 !== undefined &&
							entry.csr.score4 !== undefined
						) {
							supplierScore = Math.round((Math.round(entry.csr.score1) + Math.round(entry.csr.score2) + Math.round(entry.csr.score3) + Math.round(entry.csr.score4)) / 4)
							score1 = Math.round(entry.csr.score1)
							score2 = Math.round(entry.csr.score2)
							score3 = Math.round(entry.csr.score3)
							score4 = Math.round(entry.csr.score4)
							csrid = entry.csr.id
							if (entry.csr.timestamp) {
								const date = new Date(entry.csr.timestamp)
								const month = date.toLocaleString("default", { month: "short" })
								const year = date.getFullYear()
								filledoutdate = `${month} ${year}`
								filledoutyear = `${new Date().getFullYear()}`

							}
						}
						this.connections.push({
							clientID: entry.clientID,
							name: entry.name,
							csr: entry.csr,
							averageConnectionScores: entry.averageSupplierScores,
							noOfSuppliersFilled: entry.noOfSuppliersFilled,
							totalsuppliers: entry.totalsuppliers,
							noConnectedsuplliers: entry.noConnectedsuplliers,
							countryofoperation: entry.COO,
							remove: null,
							supplierScore: supplierScore,
							importanceofsupplier: importance,
							importanceScore: importanceScore,
							score1: score1,
							score2: score2,
							score3: score3,
							score4: score4,
							filledoutdate: filledoutdate,
							filledoutyear: filledoutyear,
							csrid: csrid,
						})
						this.importanceClientArray.push({
							importance: entry.importance,
							clientID: entry.clientID,
						})
					}
					this.getImpactSuppiler
					this.averageScore()
					this.riskImpact()
					if(this.selectedSort == "Impact Level"){
						this.sortByImportance()
					}else if(this.selectedSort == "Human rights"){
						this.sortByHumanRights()
					}else if(this.selectedSort == "Labour"){
						this.sortByLabour()
					}else if(this.selectedSort == "Environment"){
						this.sortByEnvironment()
					}else if(this.selectedSort == "Anticorruption"){
						this.sortByAnticorruption()
					}else if(this.selectedSort == "Transparency Law"){
						this.sortByTransparencyLaw()
					}else{
						this.sortByTotalScore()
					}
					
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		loadProjects() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			ConnectionAPI.getProjects()
				.then((data) => {
					this.projects = data.map(project => ({
						id: project.ProjectID,
						name: project.ProjectName,
						clientIDs: typeof project.Involved === "string" ? project.Involved.split(",").map(id => parseInt(id, 10)) : project.Involved					}))
					this.projects=[...this.projects, { id: null, name: "All Projects" }]
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		openDialog(item) {
			this.dialogItem = item // Set the clicked item data
			this.dialog = true // Open the dialog
		},
		Complete() {
			this.dialog = false
			this.dialogComplete = false
		},
		resetCheckboxes() {
			this.checkbox1 = false
			this.checkbox2 = false
			this.checkbox3 = false
			this.checkbox4 = false
		},
		async resubmit(clientID, id) {
			console.log(id)
			try {
				await CSR.getCSRresubmit(clientID, 1, id)
				this.dialogComplete = true
			} catch (reason) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					reason,
					{ root: true }
				)
			}
		},
		async notify(clientID) {
			try {
				await CSR.notifyCSR(clientID)
				this.dialogComplete = true
			} catch (reason) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					reason,
					{ root: true }
				)
			}
		},
		getImportance(importance) {
			switch (importance) {
			case 1:
				return { text: "LOW", icon: 1 }
			case 2:
				return { text: "MEDIUM", icon: 2 }
			case 3:
				return { text: "HIGH", icon: 3 }
			default:
				return {}
			}
		},
		getFilledOutDateStyle(filledoutdate) {
			return this.isDateMoreThanOneYearOld(filledoutdate)
				? { color: "red", fontWeight: "bold", paddingLeft: "20px" }
				: { paddingLeft: "20px" }
		},
		getFilledOutDateStyleind(filledoutdate) {
			return this.isDateMoreThanOneYearOld(filledoutdate)
				? { color: "red", fontWeight: "bold", margin: "0" }
				: { margin: "0" }
		},
		getFilledOutDateStyleind1(filledoutdate) {
			return this.isDateMoreThanOneYearOld(filledoutdate)
				? { color: "red", fontWeight: "bold", margin: "0" }
				: { margin: "0" }
		},
		isDateMoreThanOneYearOld(filledoutdate) {
			if (!filledoutdate || filledoutdate === "N/A") return false

			const dateParts = filledoutdate.split(" ")
			const date = new Date(`${dateParts[0]} 1, ${dateParts[1]}`)
			const oneYearAgo = new Date()
			oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)

			return date < oneYearAgo
		},
		getRiskImpactStyle(riskImpactScore) {
			let backgroundColor
			let color = "black"
			switch (riskImpactScore) {
			case "High":
				(backgroundColor = "red"), (color = "white")
				break
			case "Medium":
				(backgroundColor = "rgb(255, 152, 0)"), (color = "white")
				break
			case "Low":
				(backgroundColor = "rgb(76, 175, 80)"), (color = "white")
				break
			default:
				backgroundColor = "aqua"
			}
			return {
				marginLeft: "30px",
				backgroundColor: backgroundColor,
				color: color,
				padding: "5px 20px",
				borderRadius: "10px",
			}
		},
		handleProjectSortChange() {
			if (this.selectedProjectSort) {
				const selectedProject = this.projects.find(projects => projects.id === this.selectedProjectSort)
				if (selectedProject.clientIDs) {
					this.connections =this.allConnections.filter(connection => selectedProject.clientIDs.includes(connection.clientID))
				} else{
					this.connections = [...this.allConnections]
				}
			} else{
				this.connections = [...this.allConnections]
			}
		},
		averageScore() {
			for (let i = 0; i < this.connections.length; i++) {
				if (this.connections[i].averageConnectionScores[0] == null) {
					this.connections[i].averageScore = 0
				} else {
					let average =
						(this.connections[i].averageConnectionScores[0] +
							this.connections[i].averageConnectionScores[1] +
							this.connections[i].averageConnectionScores[2] +
							this.connections[i].averageConnectionScores[3]) /
						4
					this.connections[i].averageScore = Math.round(average)
				}
			}
		},
		riskImpact() {
			for (let i = 0; i < this.connections.length; i++) {
				if (this.connections[i].averageConnectionScores[0] == null) {
					this.connections[i].riskImpactScore = "Not Avilable"
				} else {
					let average =
						this.connections[i].averageScore * 0.3 +
						this.connections[i].supplierScore * 0.5 +
						(this.connections[i].noOfSuppliersFilled /
							this.connections[i].totalsuppliers) *
						20
					if (Math.round(average) >= 70) {
						this.connections[i].riskImpactScore = "Low"
					} else if (40 <= this.connections[i].riskImpactScore < 70) {
						this.connections[i].riskImpactScore = "Medium"
					} else {
						this.connections[i].riskImpactScore = "High"
					}
				}
			}
		},
		handleSortChange(selectedTitle) {
			const selectedItem = this.dropdownItems.find(
				(item) => item.title === selectedTitle
			)
			console.log(this.selectedSort)
			if (selectedItem) {
				selectedItem.action() // Execute the selected sorting action
			}
		},

		sortByTotalScore() {
			return this.connections.sort(function (b, a) {
				return a.supplierScore - b.supplierScore
			})
		},
		sortByHumanRights() {
			this.scoretypesort = "humanrights"
			return this.connections.sort(function (b, a) {
				return a.score1 - b.score1
			})
		},
		sortByLabour() {
			this.scoretypesort = "labour"
			return this.connections.sort(function (b, a) {
				return a.score2 - b.score2
			})
		},
		sortByImportance() {
			this.scoretypesort = "Impact of Supplier"
			return this.connections.sort(function (b, a) {
				return a.importanceScore - b.importanceScore
			})
		},
		sortByEnvironment() {
			this.scoretypesort = "environment"
			return this.connections.sort(function (b, a) {
				return a.score3 - b.score3
			})
		},
		sortByAnticorruption() {
			this.scoretypesort = "anticorruption"
			return this.connections.sort(function (b, a) {
				return a.score4 - b.score4
			})
		},
		sortByTransparencyLaw() {
			this.scoretypesort = "transparencylaw"
			return this.connections.sort(function (b, a) {
				return a.transparencyLawScore - b.transparencyLawScore
			})
		},
		getColor(impact) {
			if (impact == "HIGH") return "red"
			else if (impact == "MEDIUM") return "orange"
			else return "green"
		},
		getColorOnScore(score) {
			if (score < 30) return "red"
			else if (30 <= score && score < 60) return "orange"
			else return "green"
		},
		async logImportanceChange(item) {
			let impact = 0

			if (item.importanceofsupplier === "HIGH") {
				impact = 3
			} else if (item.importanceofsupplier === "LOW") {
				impact = 1
			} else {
				impact = 2
			}

			for (const obj of this.importanceClientArray) {
				if (obj.clientID == item.clientID) {
					if (obj.importance === null) {
						try {
							await SupplierImpactAPI.createSupplierimpact(
								item.clientID,
								impact,
								2024
							)
						} catch (error) {
							console.error("Error saving risks:", error)
							alert("An error occurred while saving the risks.")
						}
					} else {
						try {
							await SupplierImpactAPI.updateSupplierimpactById(
								item.clientID,
								impact,
								2024
							)
						} catch (error) {
							console.error("Error saving risks:", error)
							alert("An error occurred while saving the risks.")
						}
					}
					this.loadItems()
					if(this.selectedSort == "Impact Level"){
						this.sortByImportance()
					}
				}
			}
		},
	},
}
</script>

<style scoped>
.v-progress-circular {
	margin: 0rem;
	margin-left: 20px;
}

.score-circle {
	cursor: pointer;
}

.score-dialog {
	position: relative;
	padding: 28px 24px 20px;
	overflow: visible;
}

.score-dialog-close {
	position: absolute;
	top: 6px;
	right: 6px;
	z-index: 2;
}

.score-dialog-message {
	margin: 12px 8px 16px;
	line-height: 1.45;
}

.score-dialog-date {
	margin: 16px 0 8px;
}

.score-dialog-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-top: 20px;
	flex-wrap: wrap;
}

.score-dialog-btn.v-btn {
	margin-top: 0 !important;
}

.score-dialog-footer--center {
	justify-content: center;
	margin-top: 28px;
}

.score-dialog-audit {
	display: flex;
	justify-content: flex-end;
	margin: 4px 8px 12px;
}

.score-dialog-footer--again {
	margin-top: 28px;
	padding: 0 8px 8px;
}
</style>
